import { useCallback, useEffect, useRef } from 'react';

import { useRecoilValue } from 'recoil';

import { createEnquiry } from '@shared/api/enquiry/enquiry.controller';
import {
  EnquiryCreate,
  EnquiryStatus,
  EnquiryType,
} from '@shared/api/enquiry/enquiry.interface';
import { postImage } from '@shared/api/uploadImage';
import { User } from '@shared/api/user/user.interface';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { defaultApiMutationOptions } from '@shared/plugIn/apiMutation';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { useMutation, useQueryClient } from 'react-query';

export const ENQUIRY_FILES_SIZE_LIMIT = 50000000; // 50mb
export const ENQUIRY_FILE_TOO_LARGE_MESSAGE = '파일의 크기를 줄여주세요.';

/** 로그인 계정의 성함·연락처(숫자만). TelField는 숫자만 저장하고 표시할 때 하이픈을 붙인다. */
export const enquiryContactOf = (userProfile: User | null | undefined) => ({
  name: userProfile?.info?.name ?? '',
  phone: (userProfile?.info?.phoneNumber ?? '').replace(/[^0-9]/g, ''),
});

/**
 * 성함·연락처 1회 시딩 — 프로필이 처음 도착했을 때만 채우고(창 포커스 refetch로 다시 흘러들어와
 * 사용자가 고친 값을 덮어쓰지 않도록), 이미 입력한 칸은 건드리지 않는다. 채워진 뒤에도 수정 가능.
 * @param apply 폼에 값을 넣는 쪽 — 빈 칸만 채우는 규칙은 호출부가 지킨다
 */
export const useSeedEnquiryContact = (
  userProfile: User | null | undefined,
  apply: (contact: { name: string; phone: string }) => void,
) => {
  const seededRef = useRef(false);
  const applyRef = useRef(apply);
  applyRef.current = apply;
  useEffect(() => {
    if (seededRef.current) return;
    const contact = enquiryContactOf(userProfile);
    if (!contact.name && !contact.phone) return;
    seededRef.current = true;
    applyRef.current(contact);
  }, [userProfile]);
};

/** 문의 생성 DTO 한 벌 — 1:1 문의·장례식장 양식·직접입력 자동 접수가 같은 모양으로 보낸다 */
export const buildEnquiryCreateDto = (
  userProfile: User | null | undefined,
  type: EnquiryType,
  input: { name: string; phoneNumber: string; title: string; content: string },
  imageId: string | null = null,
): EnquiryCreate => ({
  user: userProfile?._id ?? '',
  type,
  name: input.name,
  phoneNumber: input.phoneNumber,
  title: input.title,
  content: input.content,
  image: imageId,
  status: EnquiryStatus.Todo,
});

export interface CreateEnquiryInput {
  name: string;
  phoneNumber: string;
  title: string;
  content: string;
  //* Enquiry.image가 단일 필드라 1장만 저장된다
  file?: File | null;
}

/**
 * 문의 접수 한 묶음 — 사진 1장 업로드 → createEnquiry → 알림 → 내 문의내역 무효화.
 * 1:1 문의 폼과 자주 묻는 문의(장례식장 양식)가 같은 접수 경로를 탄다.
 * @returns submit: 접수 성공이면 true
 */
export const useCreateEnquiry = (type: EnquiryType) => {
  const { userProfile, assetRole } = useAuth();
  const notificationInstance = useRecoilValue(notificationInstanceAtom);
  const queryClient = useQueryClient();

  const { isLoading, mutateAsync } = useMutation(defaultApiMutationOptions, {
    onSuccess: () => {
      void invalidateQueriesByUrlPart(queryClient, 'enquiry');
    },
  });

  const submit = useCallback(
    async (input: CreateEnquiryInput): Promise<boolean> => {
      let created = false;
      const run = async () => {
        let imageId: string | null = null;
        if (input.file) {
          const image = await postImage(input.file, assetRole());
          imageId = image._id;
        }
        const result = await createEnquiry(
          buildEnquiryCreateDto(userProfile, type, input, imageId),
        );
        if (result) {
          created = true;
          notificateSuccess(notificationInstance, '문의가 정상적으로 요청되었습니다.');
        } else {
          notificateError(notificationInstance, '문의가 정상적으로 요청되지 않았습니다.');
        }
      };
      await mutateAsync(run());
      return created;
    },
    [assetRole, mutateAsync, notificationInstance, type, userProfile],
  );

  return { submit, isLoading };
};
